// src/pages/WebsiteFeatures.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Slider from "@mui/material/Slider";
import Cropper from "react-easy-crop";

// Firebase Firestore/Storage imports
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";

// Import your Firebase services and crop helper
import { db, storage } from "../firebase";
import getCroppedImg from "../utils/cropImage";

const useStyles = makeStyles((theme) => ({
  form: {
    marginBottom: theme.spacing(4),
    border: "1px solid #ccc",
    padding: theme.spacing(2),
    borderRadius: 4,
  },
  input: {
    display: "block",
    marginBottom: theme.spacing(2),
  },
  imagePreview: {
    width: "100%",
    maxWidth: 300,
    marginBottom: theme.spacing(2),
  },
  thumbnail: {
    width: 60,
    height: 86, // approximately 7:10 ratio
    objectFit: "cover",
    marginRight: theme.spacing(2),
    borderRadius: 4,
  },
  orderButtons: {
    display: "flex",
    flexDirection: "column",
    marginRight: theme.spacing(1),
  },
}));

function WebsiteFeatures() {
  const classes = useStyles();
  const [features, setFeatures] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    imageUrl: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");

  // Crop states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);

  const auth = getAuth();

  // Subscribe to the "features" collection ordered by the "order" field.
  useEffect(() => {
    const q = query(collection(db, "features"), orderBy("order"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const featuresData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setFeatures(featuresData);
    });
    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // When a file is selected, show the crop dialog.
  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      const imageURL = URL.createObjectURL(file);
      setImageSrc(imageURL);
      setIsCropping(false); // Start in decision state
      setCropDialogOpen(true);
    }
  };

  // Capture the cropped area pixels.
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Save the cropped image and update the preview.
  const handleCropSave = async () => {
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedImageUrl = URL.createObjectURL(croppedBlob);
      setCroppedImage(croppedBlob);
      setFormData({ ...formData, imageUrl: croppedImageUrl });
      setCropDialogOpen(false);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  // Upload image to Firebase Storage and return its URL.
  const uploadImage = async () => {
    if (!croppedImage && !imageFile) return "";
    if (!auth.currentUser) {
      console.warn("No authenticated user found; cannot upload image.");
      throw new Error("User is not authenticated.");
    }
    const fileToUpload = croppedImage || imageFile;
    const storageRef = ref(storage, `features/${Date.now()}_${imageFile.name}`);
    await uploadBytes(storageRef, fileToUpload);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  };

  // Handle add/update feature.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!auth.currentUser) {
      setMessage("You must be signed in to add a feature.");
      return;
    }

    try {
      let { imageUrl } = formData;
      if (imageFile) {
        imageUrl = await uploadImage();
      }
      const data = {
        title: formData.title,
        description: formData.description,
        image: imageUrl,
      };

      if (editingId) {
        // For editing, just update the content (keep the existing order).
        const featureRef = doc(db, "features", editingId);
        await updateDoc(featureRef, data);
        setMessage("Feature updated successfully!");
      } else {
        // When adding, assign an "order" based on the current list.
        const newOrder = features.length ? features[features.length - 1].order + 1 : 0;
        await addDoc(collection(db, "features"), { ...data, order: newOrder });
        setMessage("Feature added successfully!");
      }

      // Reset form and cropping states.
      setFormData({ title: "", description: "", imageUrl: "" });
      setImageFile(null);
      setCroppedImage(null);
      setEditingId(null);
    } catch (error) {
      console.error("Error saving feature:", error);
      setMessage("An error occurred while saving the feature.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "features", id));
      setMessage("Feature deleted successfully!");
    } catch (error) {
      console.error("Error deleting feature:", error);
      setMessage("An error occurred while deleting the feature.");
    }
  };

  const handleEdit = (feature) => {
    const { id, title, description, image } = feature;
    setFormData({
      title,
      description,
      imageUrl: image || "",
    });
    setEditingId(id);
  };

  // Move feature up by swapping its "order" value with the previous feature.
  const handleMoveUp = async (index) => {
    if (index === 0) return; // First item cannot move up.
    const currentFeature = features[index];
    const previousFeature = features[index - 1];

    try {
      await updateDoc(doc(db, "features", currentFeature.id), {
        order: previousFeature.order,
      });
      await updateDoc(doc(db, "features", previousFeature.id), {
        order: currentFeature.order,
      });
    } catch (error) {
      console.error("Error moving feature up:", error);
      setMessage("An error occurred while reordering.");
    }
  };

  // Move feature down by swapping its "order" value with the next feature.
  const handleMoveDown = async (index) => {
    if (index === features.length - 1) return; // Last item cannot move down.
    const currentFeature = features[index];
    const nextFeature = features[index + 1];

    try {
      await updateDoc(doc(db, "features", currentFeature.id), {
        order: nextFeature.order,
      });
      await updateDoc(doc(db, "features", nextFeature.id), {
        order: currentFeature.order,
      });
    } catch (error) {
      console.error("Error moving feature down:", error);
      setMessage("An error occurred while reordering.");
    }
  };

  return (
    <Container>
      <Box my={4}>
        <Typography variant="h4" gutterBottom>
          Manage Web Content
        </Typography>
        <form onSubmit={handleSubmit} className={classes.form}>
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={4}
            required
          />
          <Typography variant="body2" color="textSecondary">
            Please upload an image with a 7:10 portrait orientation. You can crop or zoom out the image below.
          </Typography>
          <Box my={2}>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className={classes.input}
            />
            {formData.imageUrl && !imageFile && (
              <img
                src={formData.imageUrl}
                alt="Current"
                className={classes.imagePreview}
              />
            )}
          </Box>
          <Button type="submit" variant="contained" color="primary">
            {editingId ? "Update Feature" : "Add Feature"}
          </Button>
        </form>
        {message && (
          <Typography variant="subtitle1" color="secondary">
            {message}
          </Typography>
        )}
        <Box my={4}>
          <Typography variant="h5" gutterBottom>
            Existing Features
          </Typography>
          <List>
            {features.map((feature, index) => (
              <ListItem key={feature.id} divider>
                <Box className={classes.orderButtons}>
                  <IconButton
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <ArrowUpwardIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleMoveDown(index)}
                    disabled={index === features.length - 1}
                  >
                    <ArrowDownwardIcon />
                  </IconButton>
                </Box>
                <Box
                  component="img"
                  src={feature.image}
                  alt={feature.title}
                  className={classes.thumbnail}
                />
                <ListItemText
                  primary={feature.title}
                  secondary={feature.description}
                />
                <IconButton onClick={() => handleEdit(feature)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(feature.id)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      {/* Crop Dialog */}
      <Dialog
        open={cropDialogOpen}
        onClose={() => setCropDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Image Upload (Expected Ratio: 7:10 Portrait)</DialogTitle>
        <DialogContent dividers style={{ position: "relative", height: 400 }}>
          {!isCropping ? (
            <Box sx={{ position: "relative", height: "100%", textAlign: "center" }}>
              {imageSrc && (
                <img
                  src={imageSrc}
                  alt="Preview"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              )}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "2px dashed #1976d2",
                  pointerEvents: "none",
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{ position: "absolute", bottom: 16, left: 0, right: 0 }}
              >
                Expected Ratio: 7:10 Portrait
              </Typography>
            </Box>
          ) : (
            imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={7 / 10}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )
          )}
          {isCropping && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">Zoom</Typography>
              <Slider
                value={zoom}
                min={0.5}
                max={3}
                step={0.1}
                onChange={(e, newValue) => setZoom(newValue)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!isCropping ? (
            <>
              <Button onClick={() => setCropDialogOpen(false)} color="secondary">
                Use as Is
              </Button>
              <Button onClick={() => setIsCropping(true)} variant="contained" color="primary">
                Crop Image
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setIsCropping(false)} color="secondary">
                Cancel Crop
              </Button>
              <Button onClick={handleCropSave} variant="contained" color="primary">
                Save Crop
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default WebsiteFeatures;
